import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Bell,
  Send,
  Users,
  Loader2,
  CheckCircle,
  Clock,
  Mail,
  Smartphone,
  MessageSquare,
  Link2,
  Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DesktopShell } from '@/components/desktop/DesktopShell';
import { ThreePanelLayout } from '@/components/desktop/layouts/ThreePanelLayout';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

interface BroadcastLog {
  id: string;
  title: string;
  body: string;
  deepLinkUrl: string | null;
  targetAudience: string;
  deliveryMethods: string[];
  targetCount: number;
  emailSuccess: number;
  pushSuccess: number;
  inAppSuccess: number;
  readCount: number;
  status: string;
  createdAt: string;
}

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'All Attendees', description: 'Everyone registered for this event' },
  { value: 'industry', label: 'Industry', description: 'Industry partners' },
  { value: 'nps_community', label: 'NPS Community', description: 'Students, faculty, and NPS staff' },
  { value: 'alumni', label: 'NPS Alumni', description: 'Alumni attendees' },
] as const;

const AUDIENCE_LABEL: Record<string, string> = {
  all: 'All Attendees',
  industry: 'Industry',
  nps_community: 'NPS Community',
  alumni: 'NPS Alumni',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const METHOD_ICON = {
  email: <Mail className="h-3.5 w-3.5" />,
  push: <Smartphone className="h-3.5 w-3.5" />,
  in_app: <MessageSquare className="h-3.5 w-3.5" />,
} as Record<string, React.ReactNode>;

const METHOD_LABEL: Record<string, string> = {
  email: 'Email',
  push: 'Push',
  in_app: 'In-App',
};

// ─── Left Panel — Audience Info ───────────────────────────────────────────────

const AudiencePanel = (
  <div className="h-full flex flex-col">
    <div className="h-[72px] px-4 flex items-center border-b border-border bg-muted flex-shrink-0">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Users className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 className="font-semibold text-lg">Audience</h2>
          <p className="text-sm text-muted-foreground">Broadcast targets</p>
        </div>
      </div>
    </div>

    <ScrollArea className="flex-1">
      <div className="p-4 space-y-4">
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-500" />
              Audience Targeting
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Broadcasts can target all approved attendees or a specific segment: Industry partners, NPS Community (students, faculty, staff), or NPS Alumni. The sending admin is excluded from delivery to keep read statistics accurate.
            </p>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Bell className="h-4 w-4 text-primary" />
              Delivery Channels
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-start gap-2">
              <MessageSquare className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-foreground">In-App</p>
                <p className="text-xs text-muted-foreground">Real-time toast notification + notification center history. Read tracking shows how many recipients opened the message.</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Smartphone className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-foreground">Push Notifications</p>
                <p className="text-xs text-muted-foreground">OS-level alerts for users who granted permission</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Mail className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-foreground">Email</p>
                <p className="text-xs text-muted-foreground">Sent to each recipient's registered email address</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-500" />
              Delivery
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Broadcasts are processed asynchronously via a background queue in batches of 100. Large audiences may take a few minutes to complete. Delivery stats and read counts update automatically.
            </p>
          </CardContent>
        </Card>
      </div>
    </ScrollArea>
  </div>
);

// ─── Compose Dialog ───────────────────────────────────────────────────────────

interface BroadcastReach {
  inApp: number;
  push: number;
  email: number;
}

function ComposeDialog({
  open,
  onOpenChange,
  onSent,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSent: () => void;
}) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [deepLinkUrl, setDeepLinkUrl] = useState('');
  const [methods, setMethods] = useState<string[]>(['in_app']);
  const [targetAudience, setTargetAudience] = useState('all');

  const { data: reach } = useQuery({
    queryKey: ['broadcast-reach', targetAudience],
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: BroadcastReach }>(`/admin/notifications/broadcast-reach?targetAudience=${targetAudience}`);
      return res.data;
    },
    enabled: open,
  });

  const broadcast = useMutation({
    mutationFn: (data: {
      title: string;
      body: string;
      deepLinkUrl?: string;
      deliveryMethods: string[];
      targetAudience: string;
    }) =>
      api.post<{ success: boolean; data: { broadcastId: string; targetCount: number }; message: string }>(
        '/admin/notifications/broadcast',
        data
      ),
    onSuccess: (res) => {
      toast.success(res.message ?? `Broadcast queued for ${res.data?.targetCount ?? 0} recipient(s)`);
      setTitle('');
      setBody('');
      setDeepLinkUrl('');
      setMethods(['in_app']);
      setTargetAudience('all');
      onSent();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error ?? 'Failed to send broadcast');
    },
  });

  const toggleMethod = (method: string) =>
    setMethods((prev) =>
      prev.includes(method) ? prev.filter((m) => m !== method) : [...prev, method]
    );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !body.trim() || methods.length === 0) return;
    broadcast.mutate({
      title: title.trim(),
      body: body.trim(),
      deepLinkUrl: deepLinkUrl.trim() || undefined,
      deliveryMethods: methods,
      targetAudience,
    });
  };

  const reachByMethod: Record<string, number | undefined> = {
    in_app: reach?.inApp,
    push: reach?.push,
    email: reach?.email,
  };

  const deliveryOptions = [
    { id: 'in_app', label: 'In-App', icon: MessageSquare },
    { id: 'push', label: 'Push', icon: Smartphone },
    { id: 'email', label: 'Email', icon: Mail },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !broadcast.isPending && onOpenChange(o)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Compose Broadcast</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 pt-1">
          {/* Title */}
          <div className="space-y-1.5">
            <Label htmlFor="broadcast-title" className="text-sm font-medium">
              Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="broadcast-title"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 100))}
              placeholder="e.g. Schedule change — Keynote moved"
              maxLength={100}
              className="h-10"
            />
            <p className="text-xs text-muted-foreground text-right">{title.length}/100</p>
          </div>

          {/* Target Audience */}
          <div className="space-y-1.5">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block">
              Target Audience
            </span>
            <RadioGroup value={targetAudience} onValueChange={setTargetAudience} className="gap-0.5">
              {AUDIENCE_OPTIONS.map((opt) => (
                <div key={opt.value} className="flex items-center gap-1.5 py-0.5">
                  <RadioGroupItem value={opt.value} id={`audience-${opt.value}`} className="h-3.5 w-3.5" />
                  <label htmlFor={`audience-${opt.value}`} className="text-xs cursor-pointer">
                    {opt.label}
                    <span className="text-muted-foreground ml-1">— {opt.description}</span>
                  </label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Message */}
          <div className="space-y-1.5">
            <Label htmlFor="broadcast-body" className="text-sm font-medium">
              Message <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="broadcast-body"
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, 500))}
              placeholder="Write your message here..."
              rows={4}
              maxLength={500}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground text-right">{body.length}/500</p>
          </div>

          {/* Deep link */}
          <div className="space-y-1.5">
            <Label htmlFor="broadcast-url" className="text-sm font-medium flex items-center gap-1.5">
              <Link2 className="h-3.5 w-3.5" />
              Deep Link URL{' '}
              <span className="text-muted-foreground text-xs font-normal">(optional)</span>
            </Label>
            <Input
              id="broadcast-url"
              value={deepLinkUrl}
              onChange={(e) => setDeepLinkUrl(e.target.value)}
              placeholder="/schedule or https://..."
              className="h-10"
            />
            <p className="text-xs text-muted-foreground">Users can tap the notification to navigate here</p>
            <div className="bg-muted/50 rounded-lg p-3 space-y-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Common links</p>
              {[
                { path: '/schedule', desc: 'Schedule' },
                { path: '/opportunities', desc: 'Opportunities' },
                { path: '/industry', desc: 'Industry Partners' },
                { path: '/connections', desc: 'Connections' },
              ].map((item) => (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => setDeepLinkUrl(item.path)}
                  className="flex items-center justify-between w-full text-left text-xs px-2 py-0.5 rounded hover:bg-muted transition-colors"
                >
                  <code className="text-primary text-[11px]">{item.path}</code>
                  <span className="text-muted-foreground">{item.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Delivery methods */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Delivery Methods <span className="text-destructive">*</span>
            </Label>
            <div className="flex flex-wrap gap-2">
              {deliveryOptions.map(({ id, label, icon: Icon }) => (
                <label
                  key={id}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 rounded-lg border-2 cursor-pointer select-none transition-colors text-sm',
                    methods.includes(id)
                      ? 'border-primary bg-primary/5 text-primary font-medium'
                      : 'border-border text-muted-foreground hover:border-primary/50'
                  )}
                >
                  <Checkbox
                    checked={methods.includes(id)}
                    onCheckedChange={() => toggleMethod(id)}
                    className="h-4 w-4"
                  />
                  <Icon className="h-4 w-4" />
                  <span>{label}</span>
                  {reachByMethod[id] !== undefined && (
                    <span className="text-xs text-muted-foreground font-normal">
                      {reachByMethod[id]} {reachByMethod[id] === 1 ? 'user' : 'users'}
                    </span>
                  )}
                </label>
              ))}
            </div>
            {methods.length === 0 && (
              <p className="text-xs text-destructive">Select at least one delivery method</p>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={broadcast.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!title.trim() || !body.trim() || methods.length === 0 || broadcast.isPending}
              className="gap-2"
            >
              {broadcast.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Send Broadcast
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function NotificationsPage() {
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['broadcast-logs'],
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: BroadcastLog[] }>(
        '/admin/notifications/broadcasts?limit=50'
      );
      return res.data;
    },
    refetchInterval: 15_000,
  });

  const logs = data ?? [];
  const selectedLog = logs.find((l) => l.id === selectedLogId) ?? null;

  // ── Center Panel — Broadcast List ───────────────────────────────────────────
  const CenterPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center justify-between border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Send className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Broadcasts</h2>
            <p className="text-sm text-muted-foreground">
              {isLoading ? 'Loading...' : `${logs.length} message${logs.length !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => setComposeOpen(true)}>
          <Plus className="h-4 w-4" />
          Compose
        </Button>
      </div>

      {isLoading ? (
        <div className="flex-1 divide-y divide-border overflow-y-auto">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="px-4 py-3 space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-32" />
            </div>
          ))}
        </div>
      ) : logs.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-muted-foreground p-8">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Send className="h-8 w-8 text-primary/50" />
            </div>
            <p className="font-medium">No broadcasts sent yet</p>
            <p className="text-sm mt-1">Click Compose to send your first broadcast.</p>
            <Button size="sm" className="mt-4 gap-1.5" onClick={() => setComposeOpen(true)}>
              <Plus className="h-4 w-4" />
              Compose
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto divide-y divide-border">
          {logs.map((log) => (
            <div
              key={log.id}
              onClick={() => setSelectedLogId(log.id === selectedLogId ? null : log.id)}
              className={cn(
                'px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors',
                selectedLogId === log.id && 'bg-primary/5 border-l-2 border-l-primary'
              )}
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <p className="font-medium text-sm leading-snug truncate flex-1">{log.title}</p>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {log.targetAudience !== 'all' && (
                    <Badge variant="outline" className="text-[10px]">
                      {AUDIENCE_LABEL[log.targetAudience] ?? log.targetAudience}
                    </Badge>
                  )}
                  <Badge
                    variant={log.status === 'completed' ? 'default' : 'secondary'}
                    className="text-[10px]"
                  >
                    {log.status}
                  </Badge>
                </div>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2">{log.body}</p>
              <div className="flex items-center gap-2 mt-1.5">
                <div className="flex items-center gap-1">
                  {log.deliveryMethods.map((m) => {
                    const count = m === 'in_app' ? log.inAppSuccess : m === 'push' ? log.pushSuccess : log.emailSuccess;
                    return (
                      <span
                        key={m}
                        className="flex items-center gap-0.5 text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground"
                      >
                        {METHOD_ICON[m]}
                        {METHOD_LABEL[m] ?? m}
                        {log.status === 'completed' && (
                          <span className="ml-0.5 font-medium">{count} sent</span>
                        )}
                      </span>
                    );
                  })}
                </div>
                <span className="ml-auto text-[10px] text-muted-foreground flex-shrink-0">
                  {formatDistanceToNow(new Date(log.createdAt), { addSuffix: true })}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── Right Panel — Broadcast Detail ──────────────────────────────────────────
  const RightPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Bell className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Details</h2>
            <p className="text-sm text-muted-foreground">
              {selectedLog ? selectedLog.title : 'Select a broadcast'}
            </p>
          </div>
        </div>
      </div>

      {selectedLog ? (
        <ScrollArea className="flex-1">
          <div className="p-5 space-y-5">
            {/* Title & Status */}
            <div>
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-base font-bold leading-snug">{selectedLog.title}</h3>
                <Badge
                  variant={selectedLog.status === 'completed' ? 'default' : 'secondary'}
                  className="flex-shrink-0 capitalize"
                >
                  {selectedLog.status}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {format(new Date(selectedLog.createdAt), 'MMM d, yyyy · h:mm a')}
                <span className="text-muted-foreground/60 ml-1">
                  ({formatDistanceToNow(new Date(selectedLog.createdAt), { addSuffix: true })})
                </span>
              </p>
            </div>

            <Separator />

            {/* Message Body */}
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                Message
              </p>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{selectedLog.body}</p>
            </div>

            {/* Deep link */}
            {selectedLog.deepLinkUrl && (
              <>
                <Separator />
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                    <Link2 className="h-3 w-3" />
                    Deep Link
                  </p>
                  <p className="text-sm font-mono text-primary break-all">{selectedLog.deepLinkUrl}</p>
                </div>
              </>
            )}

            <Separator />

            {/* Audience */}
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                Audience
              </p>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">
                  <span className="font-semibold">{selectedLog.targetCount}</span> recipient
                  {selectedLog.targetCount !== 1 ? 's' : ''}
                  <span className="text-muted-foreground ml-1">
                    ({AUDIENCE_LABEL[selectedLog.targetAudience] ?? 'All Attendees'})
                  </span>
                </span>
              </div>
            </div>

            <Separator />

            {/* Delivery breakdown */}
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                Delivery Results
              </p>
              <div className="space-y-3">
                {selectedLog.deliveryMethods.includes('in_app') && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <MessageSquare className="h-4 w-4 text-muted-foreground" />
                      <span>In-App</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-primary transition-all"
                          style={{
                            width: selectedLog.targetCount
                              ? `${Math.round((selectedLog.inAppSuccess / selectedLog.targetCount) * 100)}%`
                              : '0%',
                          }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {selectedLog.inAppSuccess}/{selectedLog.targetCount}
                      </span>
                    </div>
                  </div>
                )}
                {selectedLog.deliveryMethods.includes('push') && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <Smartphone className="h-4 w-4 text-muted-foreground" />
                      <span>Push</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-primary transition-all"
                          style={{
                            width: selectedLog.targetCount
                              ? `${Math.round((selectedLog.pushSuccess / selectedLog.targetCount) * 100)}%`
                              : '0%',
                          }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {selectedLog.pushSuccess}/{selectedLog.targetCount}
                      </span>
                    </div>
                  </div>
                )}
                {selectedLog.deliveryMethods.includes('email') && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span>Email</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-primary transition-all"
                          style={{
                            width: selectedLog.targetCount
                              ? `${Math.round((selectedLog.emailSuccess / selectedLog.targetCount) * 100)}%`
                              : '0%',
                          }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {selectedLog.emailSuccess}/{selectedLog.targetCount}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Read Statistics */}
            {selectedLog.status === 'completed' && selectedLog.deliveryMethods.includes('in_app') && (
              <>
                <Separator />
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                    Read Statistics
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                      <span>Read</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-emerald-500 transition-all"
                          style={{
                            width: selectedLog.inAppSuccess
                              ? `${Math.round((selectedLog.readCount / selectedLog.inAppSuccess) * 100)}%`
                              : '0%',
                          }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {selectedLog.readCount}/{selectedLog.inAppSuccess}
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </ScrollArea>
      ) : (
        <div className="flex-1 flex items-center justify-center text-muted-foreground p-8">
          <div className="text-center">
            <Bell className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium">Select a broadcast</p>
            <p className="text-sm mt-1">Click any message to view its details and delivery results</p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <DesktopShell>
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-border bg-background flex-shrink-0">
            <h1 className="text-2xl font-bold">Notifications</h1>
          </div>

          <div className="flex-1 overflow-hidden">
            <ThreePanelLayout
              left={AudiencePanel}
              center={CenterPanel}
              right={RightPanel}
              leftWidth="260px"
              equalCenterRight
              connectedPanels={!!selectedLog}
            />
          </div>
        </div>
      </DesktopShell>

      <ComposeDialog
        open={composeOpen}
        onOpenChange={setComposeOpen}
        onSent={() => {
          refetch();
          setComposeOpen(false);
        }}
      />
    </>
  );
}
